// The turn plaque, drawn in the scene rather than over it.
//
// "Turn 3 - Main - Your turn" belongs between the two boards, on the table,
// where a player looks anyway. Drawn as HTML it sits in a different space to
// everything it describes: it does not catch the table's light, it does not
// move with the camera, and it reads as a debug overlay rather than as part of
// the game.
//
// It goes through the card pipeline instead of a pipeline of its own. A card is
// already a lit, bloom-lit, tone-mapped quad with rounded corners and a bend --
// exactly what a plaque wants -- so the banner is a CardDraw whose albedo is a
// canvas the text is painted into, with its foil turned off and its mask set to
// "window" so no metal is found on it.

import type { CardDraw, CardVisual } from '../gfx/renderer.ts';
import { type Mat4, mat4 } from '../gfx/math.ts';
import { MipmapGenerator, solidTexture, textureFromBitmap } from '../gfx/texture.ts';
import type { Vec3 } from './layout.ts';

/** Painted at this size and mipped down; the plaque is small on screen. */
const TEXTURE_W = 1024;
const TEXTURE_H = 256;

export interface BannerText {
  turn: number;
  phase: string;
  who: string;
  /** Tints the plaque toward the active player. */
  yours: boolean;
}

export class TurnBanner {
  private readonly device: GPUDevice;
  private readonly mipmaps: MipmapGenerator;
  private readonly canvas: OffscreenCanvas | HTMLCanvasElement;
  private readonly ctx: OffscreenCanvasRenderingContext2D | CanvasRenderingContext2D;

  /**
   * Mask and back never change: the mask is pure blue, which the card shader
   * reads as "window" -- uncovered artwork, no metal, no diffraction.
   */
  private readonly mask: GPUTexture;
  private readonly back: GPUTexture;

  private albedo: GPUTexture | null = null;
  private visual: CardVisual | null = null;
  private last = '';
  private pending = false;
  /**
   * Bumped on every upload and folded into the visual's key, so the renderer
   * drops the bind group that pointed at the texture we just destroyed.
   */
  private generation = 0;
  private readonly model: Mat4 = mat4();

  constructor(device: GPUDevice, mipmaps: MipmapGenerator) {
    this.device = device;
    this.mipmaps = mipmaps;
    this.mask = solidTexture(device, [0, 0, 255, 255]);
    this.back = solidTexture(device, [10, 12, 18, 255]);

    this.canvas = typeof OffscreenCanvas !== 'undefined'
      ? new OffscreenCanvas(TEXTURE_W, TEXTURE_H)
      : Object.assign(document.createElement('canvas'), {
        width: TEXTURE_W, height: TEXTURE_H,
      });
    const ctx = this.canvas.getContext('2d');
    if (!ctx) throw new Error('banner: no 2d context');
    this.ctx = ctx as OffscreenCanvasRenderingContext2D;
  }

  /**
   * Repaints only when the words change.
   *
   * The upload is asynchronous (`createImageBitmap`), so this returns nothing
   * and the draw keeps using the previous texture until the new one lands --
   * one stale frame on a phase change, against a stall every frame.
   */
  update(text: BannerText): void {
    const key = `${text.turn}|${text.phase}|${text.who}|${text.yours}`;
    if (key === this.last || this.pending) return;
    this.last = key;
    this.paint(text);

    this.pending = true;
    void createImageBitmap(this.canvas as ImageBitmapSource).then((bitmap) => {
      const next = textureFromBitmap(this.device, bitmap, this.mipmaps, {
        label: 'banner',
      });
      bitmap.close();
      this.albedo?.destroy();
      this.albedo = next;
      this.generation++;
      this.visual = {
        key: `banner:${this.generation}`,
        albedo: next, mask: this.mask, back: this.back,
      };
      this.pending = false;
    }).catch(() => { this.pending = false; });
  }

  private paint(text: BannerText): void {
    const c = this.ctx;
    c.clearRect(0, 0, TEXTURE_W, TEXTURE_H);

    // A dark plate: the scene is scene-referred linear light and AgX lifts
    // everything under 0.18 hard, so a plaque meant to read as near-black is
    // painted very dark indeed.
    const plate = c.createLinearGradient(0, 0, 0, TEXTURE_H);
    plate.addColorStop(0, text.yours ? '#0a0e1c' : '#120a0e');
    plate.addColorStop(1, '#03040a');
    c.fillStyle = plate;
    c.fillRect(0, 0, TEXTURE_W, TEXTURE_H);

    // A rule along the top and bottom, in the active side's colour. This is the
    // one bright element, so it is what the bloom picks up.
    const accent = text.yours ? '#7fb8ff' : '#ff8f6e';
    c.fillStyle = accent;
    c.fillRect(0, 0, TEXTURE_W, 6);
    c.fillRect(0, TEXTURE_H - 6, TEXTURE_W, 6);

    c.textAlign = 'center';
    c.textBaseline = 'middle';

    c.fillStyle = accent;
    c.font = '600 52px "Beaufort", "Trajan Pro", Georgia, serif';
    c.fillText(`TURN ${text.turn}`, TEXTURE_W * 0.22, TEXTURE_H * 0.5);

    c.fillStyle = '#b9c2d6';
    c.font = '600 66px "Beaufort", "Trajan Pro", Georgia, serif';
    c.fillText(text.phase.toUpperCase(), TEXTURE_W * 0.55, TEXTURE_H * 0.44);

    c.fillStyle = '#6b7488';
    c.font = '500 34px system-ui, sans-serif';
    c.fillText(text.who.toUpperCase(), TEXTURE_W * 0.55, TEXTURE_H * 0.76);

    // A divider between the turn number and the phase.
    c.fillStyle = '#2a3145';
    c.fillRect(TEXTURE_W * 0.36, TEXTURE_H * 0.24, 3, TEXTURE_H * 0.52);
  }

  /**
   * The plaque as a card draw, or null before its first texture has landed.
   *
   * `settled` is 1: a sign standing on the table does not breathe. The scale is
   * deliberately non-uniform -- the card mesh is portrait, and a banner is a
   * strip.
   */
  draw(position: Vec3, tilt: number, width: number): CardDraw | null {
    if (!this.visual) return null;
    return {
      visual: this.visual,
      model: modelFor(position, tilt, width, this.model),
      foilTier: 0,
      hover: 0,
      bend: -0.0008,
      bendAxis: [1, 0],
      highlight: 0,
      reveal: 1,
      seed: 0.5,
      settled: 1,
      inHand: 0,
      handDirect: 1,
      handAmbient: 1,
      tint: [0.4, 0.55, 0.9, 0],
    };
  }

  destroy(): void {
    this.albedo?.destroy();
    this.mask.destroy();
    this.back.destroy();
  }
}

// Kept here rather than imported so the banner owns its own placement maths.
function modelFor(pos: Vec3, tilt: number, width: number, out: Mat4): Mat4 {
  const s = Math.sin(tilt);
  const cx = Math.cos(tilt);
  // Card-local width 0.63 and height 0.88 map to the plaque's 4:1 strip.
  const sx = width / 0.63;
  const sy = (width / 4) / 0.88;

  out[0] = sx; out[1] = 0; out[2] = 0; out[3] = 0;
  out[4] = 0; out[5] = cx * sy; out[6] = s * sy; out[7] = 0;
  out[8] = 0; out[9] = -s; out[10] = cx; out[11] = 0;
  out[12] = pos[0]; out[13] = pos[1]; out[14] = pos[2]; out[15] = 1;
  return out;
}
